/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Tenant data error helpers.
 *
 * formatErrorForDisplay() translates by backend MESSAGE regex, not by code, so
 * the tenant-data error codes are mapped here explicitly to backendErrors.* keys
 * (common.json; resolved from the 'platform' namespace through fallbackNS).
 */
import { formatErrorForDisplay, isApiError } from '@/services/api/errors';
import type { TenantDataIssue } from '@/types/tenant-data.types';

/** Same structural type as the translate function accepted by formatErrorForDisplay. */
export type TenantDataTranslate = (key: string, options?: Record<string, unknown>) => string;

export const TENANT_DATA_ERROR_KEYS: Record<string, string> = {
  CONFIRMATION_MISMATCH: 'backendErrors.tenantConfirmationMismatch',
  INVALID_REQUEST: 'backendErrors.tenantInvalidRequest',
  TENANT_JOB_IN_PROGRESS: 'backendErrors.tenantJobInProgress',
  TENANT_SCHEMA_UNMAPPED: 'backendErrors.tenantSchemaUnmapped',
  TENANT_PURGE_BLOCKED: 'backendErrors.tenantPurgeBlocked',
  SYSTEM_ORG_PROTECTED: 'backendErrors.systemOrgProtected',
  BACKUP_CORRUPTED: 'backendErrors.backupCorrupted',
  BACKUP_SCHEMA_INCOMPATIBLE: 'backendErrors.backupSchemaIncompatible',
  BACKUP_NOT_AVAILABLE: 'backendErrors.backupNotAvailable',
  RESTORE_TARGET_NOT_EMPTY: 'backendErrors.restoreTargetNotEmpty',
  RESTORE_CONFLICT: 'backendErrors.restoreConflict',
  RESTORE_MISSING_REFERENCE: 'backendErrors.restoreMissingReference',
  RESTORE_REMAP_LIMIT_EXCEEDED: 'backendErrors.restoreRemapLimitExceeded',
};

function keyForCode(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  return Object.prototype.hasOwnProperty.call(TENANT_DATA_ERROR_KEYS, code)
    ? TENANT_DATA_ERROR_KEYS[code]
    : undefined;
}

/** Translated, user-facing message for an error thrown by a tenant-data API call. */
export function getTenantDataErrorMessage(err: unknown, t: TenantDataTranslate): string {
  if (isApiError(err)) {
    const key = keyForCode(err.code);
    if (key) return t(key);
  }
  return formatErrorForDisplay(err, t).message;
}

/** Message for a preview issue: translated code prefix + backend detail, or the raw detail. */
export function getIssueMessage(issue: TenantDataIssue, t: TenantDataTranslate): string {
  const key = keyForCode(issue.code);
  return key ? `${t(key)} — ${issue.message}` : issue.message;
}

/**
 * The API client interceptor already toasts every 409, so pages must not toast
 * those again (they render the message inline instead).
 */
export function shouldToastTenantDataError(err: unknown): boolean {
  return !(isApiError(err) && err.status === 409);
}
